import { captureTrackingParams } from "@/lib/tracking-params";

/** Cookie read that never throws during SSR. */
function readCookie(name: string): string {
  if (typeof document === "undefined") return "";
  const m = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)"),
  );
  return m ? decodeURIComponent(m[1]!) : "";
}

/**
 * Ad click IDs, Meta browser cookies and page context that server-side GTM
 * cannot reconstruct from the request alone. Every conversion event carries
 * these so Google Ads / Meta CAPI can match the lead to the click.
 */
export function attributionForEvent(): Record<string, unknown> {
  if (typeof window === "undefined") return {};
  let t: Record<string, string> = {};
  try {
    t = captureTrackingParams() as unknown as Record<string, string>;
  } catch {
    /* storage blocked — fall back to live cookies/URL below */
  }
  const url = new URLSearchParams(window.location.search);
  const pick = (k: string) => (url.get(k) || t[k] || "").slice(0, 500);

  const fbp = readCookie("_fbp") || t["fbp"] || "";
  const fbclid = pick("fbclid");
  const fbc = readCookie("_fbc") || t["fbc"] || (fbclid ? `fb.1.${Date.now()}.${fbclid}` : "");

  return {
    // Meta browser identifiers (Advanced Matching / CAPI dedupe)
    fbp,
    fbc,
    fbclid,
    // Google / Microsoft / TikTok click IDs
    gclid: pick("gclid"),
    gbraid: pick("gbraid"),
    wbraid: pick("wbraid"),
    gclsrc: pick("gclsrc"),
    gad_source: pick("gad_source"),
    msclkid: pick("msclkid"),
    ttclid: pick("ttclid"),
    // Campaign context
    utm_source: pick("utm_source"),
    utm_medium: pick("utm_medium"),
    utm_campaign: pick("utm_campaign"),
    utm_content: pick("utm_content"),
    utm_term: pick("utm_term"),
    ga_client_id: t["ga_client_id"] || "",
    traffic_source: t["source"] || "",
    // Page context (server-side container can't see the SPA URL reliably)
    page_location: window.location.href,
    page_referrer: t["referrer"] || document.referrer || "",
    page_title: document.title,
    landing_page: t["landing_page"] || "",
    client_user_agent: navigator.userAgent,
  };
}

/**
 * Pushes a conversion event to the GTM dataLayer and waits until the container
 * reports every tag has fired (`eventCallback`), with a hard timeout so a
 * blocked/absent container never stalls the redirect.
 *
 * Forms that navigate right after submitting MUST await this — otherwise the
 * page unloads before GTM can send the GA4 / Ads / Meta requests and the lead
 * never shows up in those platforms.
 */
export function pushLeadEvent(
  payload: Record<string, unknown>,
  timeoutMs = 2000,
): Promise<void> {
  return new Promise<void>((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    const timer = window.setTimeout(finish, timeoutMs);
    try {
      const w = window as unknown as { dataLayer?: unknown[] };
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push({
        ...attributionForEvent(),
        ...payload,
        eventTimeout: timeoutMs,
        eventCallback: () => {
          window.clearTimeout(timer);
          finish();
        },
      });
    } catch {
      window.clearTimeout(timer);
      finish();
    }
  });
}
